import { supabase } from "@/integrations/supabase/client";

export interface Product {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price_per_m: string;
  active: boolean;
  created_at: string;
  uses_external_builder?: boolean;
  builder_size_id?: number | null;
  is_gangsheet?: boolean;
  allow_manual_upload?: boolean;
}

export interface AdditionalSection {
  title: string;
  description: string;
}

export interface RichProduct extends Product {
  short_description: string | null;
  full_description: string | null;
  product_type: string | null;
  image_main_url: string | null;
  image_gallery_urls: string[];
  tags: string[];
  collections: string[];
  ribbon_enabled: boolean;
  ribbon_text: string | null;
  additional_sections: AdditionalSection[];
  is_gangsheet: boolean;
  allow_manual_upload: boolean;
  shipping_use_global: boolean;
  shipping_config: Record<string, unknown> | null;
  variants: unknown[];
  status: "draft" | "scheduled" | "live";
  go_live_at: string | null;
  sort_order: number;
  slug: string | null;
  updated_at: string;
  tax_use_global?: boolean;
  tax_rate_override?: number | null;
}

export interface ShopSettings {
  id: number;
  hero_title: string;
  hero_subtitle: string;
  hero_cta_text: string;
  featured_product_ids: string[];
  hero_image_url: string | null;
  updated_at: string;
  // Theme controls
  background_style?: string | null; // 'solid' | 'gradient' | 'image'
  background_color?: string | null;
  background_gradient_enabled?: boolean | null;
  background_gradient_from?: string | null;
  background_gradient_to?: string | null;
  // Featured layout: 'grid' | 'bento' | 'carousel'
  featured_layout_mode?: string | null;
  // Ribbon styling
  ribbon_style?: string | null;
  ribbon_color?: string | null;
  ribbon_gradient_from?: string | null;
  ribbon_gradient_to?: string | null;
  // Product card display: 'compact' | 'standard' | 'large'
  product_card_style?: string | null;
  // CTA button visibility
  hero_cta_enabled?: boolean | null;
  // FlightDeck AI toggle
  flightdeck_ai_enabled?: boolean | null;
  // Layout / hero
  hero_full_width?: boolean | null;
  hero_sticky_header?: boolean | null;
  hero_height_px?: number | null;
  hero_elements?: HeroElement[] | null;
  hero_border_radius?: number | null;
  // Section spacing
  gap_hero_featured_px?: number | null;
  gap_featured_products_px?: number | null;
  // Page builder
  use_builder?: boolean | null;
  builder_rows?: BuilderRow[] | null;
}

// ─── Page builder model ──────────────────────────────────────────────────────

export type BuilderWidgetType =
  | "hero"
  | "featured"
  | "product_grid"
  | "heading"
  | "text"
  | "image"
  | "spacer"
  | "cta";

export interface BuilderWidget {
  id: string;
  type: BuilderWidgetType;
  // Per-type props bag, kept loose so we can iterate without migrations.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>;
}

export interface BuilderColumn {
  id: string;
  span: number; // out of 12
  widgets: BuilderWidget[];
}

export interface BuilderRow {
  id: string;
  columns: BuilderColumn[];
  // Optional row-level styling
  background?: string | null;
  padding_y?: number | null;
  gap_px?: number | null;
  max_width?: "site" | "wide" | "full" | null;
}

export interface HeroElement {
  id: "title" | "subtitle" | "cta" | "ai";
  visible: boolean;
  order: number;
}

export interface ShippingMethod {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price: number;
  active: boolean;
  created_at: string;
}

export interface CuttingTier {
  id: string;
  code: string;
  name: string;
  min_items: number;
  max_items: number | null;
  price_per_m: number;
  description: string | null;
  active: boolean;
  created_at: string;
}

export async function listProducts(): Promise<Product[]> {
  const { data, error } = await supabase
    .from("products")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) throw new Error(`Failed to fetch products: ${error.message}`);
  return data || [];
}

export async function listActiveProducts(): Promise<Product[]> {
  const { data, error } = await supabase
    .from("products")
    .select("*")
    .eq("active", true)
    .order("created_at", { ascending: false });

  if (error) throw new Error(`Failed to fetch products: ${error.message}`);
  return data || [];
}

export async function createProduct(input: {
  code: string;
  name: string;
  description?: string;
  price_per_m: string;
  uses_external_builder?: boolean;
  builder_size_id?: number | null;
}): Promise<Product> {
  const { data, error } = await supabase.from("products").insert(input).select().single();

  if (error) throw new Error(`Failed to create product: ${error.message}`);
  return data;
}

export async function updateProduct(
  id: string,
  patch: Partial<Pick<Product, "name" | "description" | "price_per_m" | "active">>,
): Promise<Product> {
  const { data, error } = await supabase
    .from("products")
    .update(patch)
    .eq("id", id)
    .select()
    .single();

  if (error) throw new Error(`Failed to update product: ${error.message}`);
  return data;
}

export async function toggleProductActive(id: string, active: boolean): Promise<Product> {
  return updateProduct(id, { active });
}

export async function deleteProduct(id: string): Promise<void> {
  const { error } = await supabase.from("products").delete().eq("id", id);

  if (error) throw new Error(`Failed to delete product: ${error.message}`);
}

export async function listShippingMethods(): Promise<ShippingMethod[]> {
  const { data, error } = await supabase
    .from("shipping_methods")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) throw new Error(`Failed to fetch shipping methods: ${error.message}`);
  return data || [];
}

export async function listActiveShippingMethods(): Promise<ShippingMethod[]> {
  const { data, error } = await supabase
    .from("shipping_methods")
    .select("*")
    .eq("active", true)
    .order("created_at", { ascending: false });

  if (error) throw new Error(`Failed to fetch shipping methods: ${error.message}`);
  return data || [];
}

export async function createShippingMethod(input: {
  code: string;
  name: string;
  description?: string;
  price: number;
}): Promise<ShippingMethod> {
  const { data, error } = await supabase.from("shipping_methods").insert(input).select().single();

  if (error) throw new Error(`Failed to create shipping method: ${error.message}`);
  return data;
}

export async function updateShippingMethod(
  id: string,
  patch: Partial<Pick<ShippingMethod, "name" | "description" | "price" | "active">>,
): Promise<ShippingMethod> {
  const { data, error } = await supabase
    .from("shipping_methods")
    .update(patch)
    .eq("id", id)
    .select()
    .single();

  if (error) throw new Error(`Failed to update shipping method: ${error.message}`);
  return data;
}

export async function toggleShippingActive(id: string, active: boolean): Promise<ShippingMethod> {
  return updateShippingMethod(id, { active });
}

export async function deleteShippingMethod(id: string): Promise<void> {
  const { error } = await supabase.from("shipping_methods").delete().eq("id", id);

  if (error) throw new Error(`Failed to delete shipping method: ${error.message}`);
}

export async function listCuttingTiers(): Promise<CuttingTier[]> {
  const { data, error } = await (supabase as any)
    .from("cutting_tiers")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) throw new Error(`Failed to fetch cutting tiers: ${error.message}`);
  return data || [];
}

export async function listActiveCuttingTiers(): Promise<CuttingTier[]> {
  const { data, error } = await (supabase as any)
    .from("cutting_tiers")
    .select("*")
    .eq("active", true)
    .order("min_items", { ascending: true });

  if (error) throw new Error(`Failed to fetch cutting tiers: ${error.message}`);
  return data || [];
}

export async function createCuttingTier(input: {
  code: string;
  name: string;
  min_items: number;
  max_items?: number;
  price_per_m: number;
  description?: string;
}): Promise<CuttingTier> {
  const { data, error } = await (supabase as any)
    .from("cutting_tiers")
    .insert(input)
    .select()
    .single();

  if (error) throw new Error(`Failed to create cutting tier: ${error.message}`);
  return data;
}

export async function updateCuttingTier(
  id: string,
  patch: Partial<
    Pick<CuttingTier, "name" | "min_items" | "max_items" | "price_per_m" | "description" | "active">
  >,
): Promise<CuttingTier> {
  const { data, error } = await (supabase as any)
    .from("cutting_tiers")
    .update(patch)
    .eq("id", id)
    .select()
    .single();

  if (error) throw new Error(`Failed to update cutting tier: ${error.message}`);
  return data;
}

export async function toggleCuttingTierActive(id: string, active: boolean): Promise<CuttingTier> {
  return updateCuttingTier(id, { active });
}

export async function deleteCuttingTier(id: string): Promise<void> {
  const { error } = await (supabase as any).from("cutting_tiers").delete().eq("id", id);

  if (error) throw new Error(`Failed to delete cutting tier: ${error.message}`);
}

// ─── Rich Products ────────────────────────────────────────────────────────────

export async function listRichProducts(): Promise<RichProduct[]> {
  const { data, error } = await (supabase as any)
    .from("products")
    .select("*")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map(normaliseRichProduct);
}

export async function listLiveProducts(): Promise<RichProduct[]> {
  const { data, error } = await (supabase as any)
    .from("products")
    .select("*")
    .eq("status", "live")
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map(normaliseRichProduct);
}

export async function getRichProduct(id: string): Promise<RichProduct> {
  const { data, error } = await (supabase as any)
    .from("products")
    .select("*")
    .eq("id", id)
    .single();
  if (error) throw new Error(error.message);
  return normaliseRichProduct(data);
}

export async function createRichProduct(
  input: Partial<Omit<RichProduct, "id" | "created_at" | "updated_at">>,
): Promise<RichProduct> {
  const { data, error } = await (supabase as any)
    .from("products")
    .insert({ code: `p-${Date.now()}`, price_per_m: "0", active: false, ...input })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return normaliseRichProduct(data);
}

export async function updateRichProduct(
  id: string,
  patch: Partial<Omit<RichProduct, "id" | "created_at">>,
): Promise<RichProduct> {
  const { data, error } = await (supabase as any)
    .from("products")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return normaliseRichProduct(data);
}

export async function publishProduct(id: string): Promise<RichProduct> {
  return updateRichProduct(id, { status: "live", go_live_at: null, active: true });
}

export async function draftProduct(id: string): Promise<RichProduct> {
  return updateRichProduct(id, { status: "draft", go_live_at: null, active: false });
}

export async function scheduleProduct(id: string, goLiveAt: string): Promise<RichProduct> {
  return updateRichProduct(id, { status: "scheduled", go_live_at: goLiveAt, active: false });
}

function normaliseRichProduct(row: any): RichProduct {
  return {
    ...row,
    image_gallery_urls: Array.isArray(row.image_gallery_urls) ? row.image_gallery_urls : [],
    tags: Array.isArray(row.tags) ? row.tags : [],
    collections: Array.isArray(row.collections) ? row.collections : [],
    additional_sections: Array.isArray(row.additional_sections) ? row.additional_sections : [],
    variants: Array.isArray(row.variants) ? row.variants : [],
    ribbon_enabled: row.ribbon_enabled ?? false,
    is_gangsheet: row.is_gangsheet ?? false,
    allow_manual_upload: row.allow_manual_upload ?? false,
    shipping_use_global: row.shipping_use_global ?? true,
    status: row.status ?? "draft",
    sort_order: row.sort_order ?? 0,
    tax_use_global: row.tax_use_global ?? true,
    tax_rate_override: row.tax_rate_override ?? null,
  };
}

// ─── Shop Settings ────────────────────────────────────────────────────────────

export async function getShopSettings(): Promise<ShopSettings> {
  const { data, error } = await (supabase as any)
    .from("shop_settings")
    .select("*")
    .eq("id", 1)
    .single();
  if (error) throw new Error(error.message);
  return {
    ...data,
    featured_product_ids: Array.isArray(data.featured_product_ids) ? data.featured_product_ids : [],
    builder_rows: Array.isArray(data.builder_rows) ? data.builder_rows : [],
    use_builder: !!data.use_builder,
    hero_elements:
      Array.isArray(data.hero_elements) && data.hero_elements.length > 0
        ? data.hero_elements
        : [
            { id: "title", visible: true, order: 0 },
            { id: "subtitle", visible: true, order: 1 },
            { id: "cta", visible: true, order: 2 },
            { id: "ai", visible: true, order: 3 },
          ],
  };
}

export async function updateShopSettings(
  patch: Partial<Omit<ShopSettings, "id" | "updated_at">>,
): Promise<ShopSettings> {
  const { data, error } = await (supabase as any)
    .from("shop_settings")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", 1)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}
